import { LoadingSpinner } from "@/components/loading-spinner";
import { dashboardApi } from "@/http/api";
import { useUser } from "@/http/api-hooks";
import { User } from "@/http/api-types";
import { MissingAuthTokenError } from "@/http/auth";
import { clearAuth0AccessToken, setAuth0AccessToken } from "@/http/auth0-token";
import { useMixpanel } from "@/lib/mixpanel/mixpanel-context";
import { useAuth0 } from "@auth0/auth0-react";
import { AxiosError, InternalAxiosRequestConfig } from "axios";
import { createContext, useContext, useEffect, useState } from "react";

type AuthContextType =
  | {
      isLoading: boolean;
      isAuthenticated: false;
    }
  | {
      isLoading: boolean;
      isAuthenticated: true;
      user: User;
    };

const defaultAuthState: AuthContextType = {
  isLoading: true,
  isAuthenticated: false,
};

const AuthContext = createContext<AuthContextType>(defaultAuthState);

// Fetches /api/user and populates AuthContext. Only mounted once the Bearer
// token is in memory, so useUser() never fires without a token.
function UserContextProvider({ children }: { children: React.ReactNode }) {
  const { data, isLoading, error } = useUser();
  const { identify, reset } = useMixpanel();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error instanceof MissingAuthTokenError || !data) {
    reset();
    return null;
  }

  if (error) {
    reset();
    throw new Error(error?.message || "Failed to authenticate user");
  }

  if (data?.user) {
    identify({
      id: data.user.id,
      name: data.user.name,
      admin: data.user.admin,
    });
  }

  return (
    <AuthContext.Provider value={{ isLoading: false, isAuthenticated: true, user: data.user }}>
      {children}
    </AuthContext.Provider>
  );
}

function AuthProvider({ children }: { children: React.ReactNode }) {
  const { getAccessTokenSilently, loginWithRedirect, isLoading, isAuthenticated } = useAuth0();
  const [tokenReady, setTokenReady] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname + window.location.search + window.location.hash },
      });
      return;
    }

    const interceptorId = dashboardApi.interceptors.response.use(undefined, async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const token = await getAccessTokenSilently();
          setAuth0AccessToken(token);
          return dashboardApi({
            ...originalRequest,
            headers: { ...originalRequest.headers, Authorization: `Bearer ${token}` },
          });
        } catch {
          clearAuth0AccessToken();
          loginWithRedirect({
            appState: { returnTo: window.location.pathname + window.location.search + window.location.hash },
          });
        }
      }
      return Promise.reject(error);
    });

    getAccessTokenSilently()
      .then(token => {
        setAuth0AccessToken(token);
        return dashboardApi.post("/api/auth0/provision");
      })
      .then(() => setTokenReady(true))
      .catch((error: unknown) => {
        if (error instanceof AxiosError) {
          // Only redirect on auth errors; other errors (e.g. 500) would cause an infinite redirect loop
          if (error.response?.status === 401) {
            loginWithRedirect({
              appState: { returnTo: window.location.pathname + window.location.search + window.location.hash },
            });
          } else {
            throw error;
          }
        } else {
          // Auth0 token error — redirect to login
          loginWithRedirect({
            appState: { returnTo: window.location.pathname + window.location.search + window.location.hash },
          });
        }
      });

    return () => dashboardApi.interceptors.response.eject(interceptorId);
  }, [isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  if (!tokenReady) {
    return <LoadingSpinner />;
  }

  return <UserContextProvider>{children}</UserContextProvider>;
}

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
